import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenLabel } from "@/components/brand/TokenAmount";
import {
	useBuyMmkWithIcp,
	useBuyMmkWithUsdm,
	useGasFundingStatus,
	useMmkSwapParams,
	useQuoteMmk,
	useQuoteMmkWithIcp,
} from "@/hooks/useGovernance";
import { useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import {
	mmkMultipleFromStart,
	mmkOutForUsdm,
	mmkProgressPct,
	mmkSpotPrice,
} from "@/lib/mmk-curve";
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { cn, formatAmountPlain, formatNum, parseAmount } from "@/lib/utils";
import { useMemo, useState } from "react";

type PayAsset = "USDM" | "ICP";

/** Power-law only-up USDM → MMK desk + Gas Funding ICP → MMK. */
export function MmkSwapWidget() {
	const { balances } = useAuth();
	const { data: params, isLoading } = useMmkSwapParams();
	const { data: gas } = useGasFundingStatus();
	const buyUsdm = useBuyMmkWithUsdm();
	const buyIcp = useBuyMmkWithIcp();
	const [pay, setPay] = useState<PayAsset>("USDM");
	const [usdmAmount, setUsdmAmount] = useState("10");
	const [icpAmount, setIcpAmount] = useState("1");
	const usdmIn = parseAmount(usdmAmount);
	const icpIn = parseAmount(icpAmount);
	const { data: onChainQuote } = useQuoteMmk(usdmIn);
	const { data: icpQuote } = useQuoteMmkWithIcp(icpIn);

	const sold = params?.totalSoldMmk ?? 0;
	const cap = params?.capMmk ?? 95_000_000;
	const remaining = Math.max(0, cap - sold);
	const spot = mmkSpotPrice(sold);
	const progress = mmkProgressPct(sold, cap);
	const multiple = mmkMultipleFromStart(sold);

	const maxUsdm = depositableTokenMax(balances.USDM);
	const maxIcp = depositableTokenMax(balances.ICP);
	const discountBps = gas?.discountBps ?? 500;
	const discountPct = discountBps / 100;

	const preview = useMemo(() => {
		if (usdmIn <= 0) return { mmkOut: 0, avg: 0 };
		let mmkOut = onChainQuote ?? mmkOutForUsdm(sold, usdmIn);
		if (mmkOut > remaining) mmkOut = remaining;
		const avg = mmkOut > 0 ? usdmIn / mmkOut : 0;
		return { mmkOut, avg };
	}, [usdmIn, sold, remaining, onChainQuote]);

	const icpPreview = useMemo(() => {
		if (icpIn <= 0) return { mmkOut: 0 };
		const mmkOut = icpQuote ?? 0;
		return { mmkOut };
	}, [icpIn, icpQuote]);

	if (isLoading) {
		return <div className="skeleton-shimmer h-48 rounded-xl" />;
	}

	if (!params) {
		return (
			<div className="card-surface p-5 text-sm text-muted-foreground">
				MMK only-up swap unavailable (protocol env required).
			</div>
		);
	}

	const gasOpen = Boolean(gas?.open);

	return (
		<div className="card-surface space-y-4 border-primary/20 p-5">
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div className="flex min-w-0 items-start gap-3">
					<div className="flex items-center gap-1.5 pt-0.5">
						{pay === "USDM" ? (
							<BrandTokenMark token="USDM" size="md" />
						) : (
							<TokenIcon token="ICP" size="md" />
						)}
						<span className="text-muted-foreground">→</span>
						<BrandTokenMark token="MMK" size="md" />
					</div>
					<div>
						<p className="section-eyebrow">Only-up power law</p>
						<h2 className="text-lg font-bold">Get MMK</h2>
						<p className="mt-1 text-xs text-muted-foreground">
							USDM desk advances the only-up curve. ICP Gas Funding buys MMK from
							treasury inventory at a {discountPct}% discount under mark while
							ops ICP is under the {formatNum(gas?.gasTargetIcp ?? 1000, 0)} ICP
							target — proceeds fund cycles reserves.
						</p>
					</div>
				</div>
				<span
					className={
						params.enabled
							? "rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"
							: "rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-semibold text-warning"
					}
				>
					{params.enabled ? "Live" : "Paused"}
				</span>
			</div>

			<div className="grid w-full grid-cols-2 gap-2 sm:max-w-md">
				{(
					[
						["USDM", "Pay with USDM", "Only-up curve"],
						["ICP", "Pay with ICP", "Gas Funding"],
					] as const
				).map(([id, label, sub]) => (
					<button
						key={id}
						type="button"
						onClick={() => setPay(id)}
						className={cn(
							"flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl border-2 px-3 py-2.5 transition-colors",
							pay === id
								? "border-primary bg-primary/15 text-primary shadow-[0_0_12px_rgba(0,200,83,0.15)]"
								: "border-border bg-muted/20 text-muted-foreground hover:border-border/80 hover:bg-muted/40",
						)}
					>
						<span className="inline-flex items-center gap-2">
							<TokenIcon token={id} size="lg" plain />
							<span className="text-sm font-bold sm:text-base">{label}</span>
						</span>
						<span
							className={cn(
								"text-[10px] font-medium",
								pay === id ? "text-primary/80" : "text-muted-foreground",
							)}
						>
							{sub}
						</span>
					</button>
				))}
			</div>

			<div className="grid gap-3 sm:grid-cols-3">
				<div className="rounded-xl border border-border bg-muted/30 p-3">
					<p className="text-[10px] uppercase text-muted-foreground">Spot</p>
					<p className="font-mono-amount text-lg font-bold">
						{formatNum(spot, 4)} USDM
					</p>
					<p className="text-[10px] text-muted-foreground">
						{multiple.toFixed(2)}× from start
					</p>
				</div>
				<div className="rounded-xl border border-border bg-muted/30 p-3">
					<p className="text-[10px] uppercase text-muted-foreground">Sold</p>
					<p className="font-mono-amount text-lg font-bold">
						{formatNum(sold, 0)} MMK
					</p>
				</div>
				<div className="rounded-xl border border-border bg-muted/30 p-3">
					<p className="text-[10px] uppercase text-muted-foreground">Cap left</p>
					<p className="font-mono-amount text-lg font-bold">
						{formatNum(remaining, 0)}
					</p>
				</div>
			</div>

			<div>
				<div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
					<span>Ownership sold</span>
					<span>{progress.toFixed(1)}%</span>
				</div>
				<div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
					<div
						className="h-full rounded-full bg-primary shadow-[0_0_8px_rgba(0,200,83,0.4)]"
						style={{ width: `${progress}%` }}
					/>
				</div>
			</div>

			{pay === "USDM" ? (
				<>
					<label className="block">
						<span className="text-xs font-semibold text-muted-foreground">
							USDM to spend
						</span>
						<div className="mt-1 flex items-center gap-2">
							<input
								value={usdmAmount}
								onChange={(e) => setUsdmAmount(e.target.value)}
								inputMode="decimal"
								className="h-12 flex-1 rounded-xl border border-border bg-muted/40 px-3 font-mono-amount text-lg outline-none focus:border-primary"
							/>
							<TokenLabel id="USDM" size="md" />
						</div>
						<p className="mt-1 text-[11px] text-muted-foreground">
							Wallet{" "}
							<span className="balance-chip">
								{formatNum(balances.USDM, 2)} USDM
							</span>
							<button
								type="button"
								className="ml-2 font-semibold text-primary"
								onClick={() =>
									setUsdmAmount(formatAmountPlain(Math.max(0, maxUsdm), 2))
								}
							>
								Max
							</button>
						</p>
					</label>

					<div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm">
						<p className="text-xs text-muted-foreground">You receive (est.)</p>
						<div className="mt-1 flex items-center gap-2">
							<span className="font-mono-amount text-2xl font-bold">
								{formatNum(preview.mmkOut, 4)}
							</span>
							<TokenLabel id="MMK" size="md" />
						</div>
						{preview.mmkOut > 0 ? (
							<p className="mt-1 text-[11px] text-muted-foreground">
								Avg ~{formatNum(preview.avg, 4)} USDM/MMK
							</p>
						) : null}
					</div>

					<RequireAuthAction
						labelWhenUnauthed="Connect to buy MMK"
						disabled={
							buyUsdm.isPending ||
							!params.enabled ||
							usdmIn <= 0 ||
							usdmIn > maxUsdm + 1e-9
						}
						onAuthenticated={() => buyUsdm.mutate(usdmIn)}
					>
						{buyUsdm.isPending ? "Buying…" : "Buy MMK with USDM"}
					</RequireAuthAction>
				</>
			) : (
				<>
					{gasOpen ? (
						<div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-muted-foreground">
							<strong className="text-foreground">Gas Funding open</strong> —
							ops ICP {formatNum(gas?.opsIcp ?? 0, 2)} /{" "}
							{formatNum(gas?.gasTargetIcp ?? 1000, 0)} target. MMK from treasury
							at {discountPct}% under mark. Closes at target; reopens below{" "}
							{formatNum(gas?.gasFloorIcp ?? 250, 0)} ICP floor.
						</div>
					) : (
						<div className="rounded-xl border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
							Gas Funding closed — ops ICP reserve at or above the{" "}
							{formatNum(gas?.gasTargetIcp ?? 1000, 0)} ICP cycles buffer (or
							waiting to drop below the floor after a close).
						</div>
					)}

					<label className="block">
						<span className="text-xs font-semibold text-muted-foreground">
							ICP to spend
						</span>
						<div className="mt-1 flex items-center gap-2">
							<input
								value={icpAmount}
								onChange={(e) => setIcpAmount(e.target.value)}
								inputMode="decimal"
								disabled={!gasOpen}
								className="h-12 flex-1 rounded-xl border border-border bg-muted/40 px-3 font-mono-amount text-lg outline-none focus:border-primary disabled:opacity-50"
							/>
							<TokenLabel id="ICP" size="md" />
						</div>
						<p className="mt-1 text-[11px] text-muted-foreground">
							Wallet{" "}
							<span className="balance-chip">
								{formatNum(balances.ICP, 2)} ICP
							</span>
							<button
								type="button"
								className="ml-2 font-semibold text-primary disabled:opacity-40"
								disabled={!gasOpen || maxIcp <= 0}
								onClick={() =>
									setIcpAmount(formatAmountPlain(Math.max(0, maxIcp), 4))
								}
							>
								Max
							</button>
						</p>
					</label>

					<div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm">
						<p className="text-xs text-muted-foreground">
							You receive (est. · {discountPct}% under mark)
						</p>
						<div className="mt-1 flex items-center gap-2">
							<span className="font-mono-amount text-2xl font-bold">
								{formatNum(icpPreview.mmkOut, 4)}
							</span>
							<TokenLabel id="MMK" size="md" />
						</div>
						<p className="mt-1 text-[11px] text-muted-foreground">
							Treasury MMK inventory:{" "}
							{formatNum(gas?.treasuryMmk ?? 0, 0)} · does not advance only-up
							sold
						</p>
					</div>

					<RequireAuthAction
						labelWhenUnauthed="Connect to buy MMK with ICP"
						disabled={
							buyIcp.isPending ||
							!gasOpen ||
							icpIn <= 0 ||
							icpIn > maxIcp + 1e-9 ||
							(gas?.treasuryMmk ?? 0) <= 0
						}
						onAuthenticated={() => buyIcp.mutate(icpIn)}
					>
						{buyIcp.isPending ? "Buying…" : "Buy MMK with ICP"}
					</RequireAuthAction>
				</>
			)}
		</div>
	);
}
